import API from '../../config/axiosConfig';
import { mockCases } from "../../utils/mockData"; // Keep for mocked functions

// Helper to create FormData for create/update operations
// Modified to handle 'newFiles' and 'deletedFiles' for updates
const createCaseFormData = (caseData, isUpdate = false) => {
  const formData = new FormData();
  const filesKey = isUpdate ? 'newFiles' : 'files'; // Key for files depends on create/update

  // Append non-file fields
  Object.keys(caseData).forEach(key => {
    // Skip file keys ('files' for create, 'newFiles' for update) and 'deletedFiles' for now
    if (key !== 'files' && key !== 'newFiles' && key !== 'deletedFiles' && caseData[key] !== undefined && caseData[key] !== null) {
      // Handle arrays (like tags): Always send as JSON string for consistency.
      // Server-side create and update methods need to handle parsing this string.
      if (Array.isArray(caseData[key])) {
        formData.append(key, JSON.stringify(caseData[key])); // Send "[]" for empty arrays
      } else {
        formData.append(key, caseData[key]); // Append simple fields
      }
    }
  });

  // Append files (either 'files' for create or 'newFiles' for update)
  // console.log(`createCaseFormData - isUpdate: ${isUpdate}, filesKey: ${filesKey}, caseData.${filesKey}:`, caseData[filesKey]);
  if (caseData[filesKey] && Array.isArray(caseData[filesKey]) && caseData[filesKey].length > 0) {
    caseData[filesKey].forEach((file, index) => {
      // console.log(`createCaseFormData - Appending file ${index}:`, file);
      if (file instanceof File) {
        // console.log(`File ${index} is a File object: name=${file.name}, size=${file.size}, type=${file.type}`);
        // Server expects files under the 'files' field name for both create and update
        formData.append('files', file, file.name);
      } else {
        // console.error(`File ${index} is NOT a File object:`, file);
      }
    });
  } else {
    // console.log(`createCaseFormData - No files to append for key ${filesKey}. caseData.${filesKey}:`, caseData[filesKey]);
  }

  // Append deletedFiles array as a JSON string if it's an update and the array exists
  if (isUpdate && caseData.deletedFiles && Array.isArray(caseData.deletedFiles)) {
    formData.append('deletedFiles', JSON.stringify(caseData.deletedFiles));
  }

  return formData;
};


export const casesApi = {
  get: async (id) => {
    // Get case by id - Maps to GET /api/medical-cases/:id
    try {
      const response = await API.get(`/api/medical-cases/${id}`);
      // The server model uses _id, ensure client uses the correct identifier
      // Assuming the response.data is the medical case object
      return response.data;
    } catch (error) {
      console.error(`Error fetching case ${id}:`, error);
      throw error; // Re-throw to be handled by calling component or global handler
    }
  },

  // Updated list function to use the real API endpoint
  list: async (filters = {}, pagination = { page: 1, limit: 10 }) => {
    // Maps to GET /api/medical-cases with query parameters
    try {
      // Separate searchTerm from other filters to ensure correct parameter name
      const { searchTerm, ...otherFilters } = filters;
      const params = {
        ...otherFilters, // Include other filters like tags, hospital etc.
        page: pagination.page,
        limit: pagination.limit,
      };
      // Add searchTerm if it exists and is not empty
      if (searchTerm) {
        params.searchTerm = searchTerm;
      }
      // Remove undefined/null filters before sending
      Object.keys(params).forEach(key => (params[key] == null || params[key] === '') && delete params[key]);

      const response = await API.get('/api/medical-cases', { params });
      // The server endpoint likely returns an object with docs, totalDocs, page, etc.
      return response.data;
    } catch (error) {
      console.error('Error fetching case list:', error);
      throw error;
    }
  },

  create: async (caseData) => {
    // Create new case - Maps to POST /api/medical-cases
    try {
      // Pass false for isUpdate
      const formData = createCaseFormData(caseData, false);
      // handlingErrorLocally: AddEditMedicalCase renders the failure inline next
      // to the save button; the floating toast would say the same thing and
      // then disappear while the form still looks unsaved.
      const response = await API.post('/api/medical-cases', formData, {
        handlingErrorLocally: true,
      });
      return response.data; // Return the newly created case object from server
    } catch (error) {
      console.error('Error creating case:', error);
      throw error;
    }
  },

  update: async (id, caseData) => {
    // Update existing case - Maps to PUT /api/medical-cases/:id
    try {
      // Pass true for isUpdate to handle 'newFiles' and 'deletedFiles'
      const formData = createCaseFormData(caseData, true);
      // See create() — the save failure is rendered inline by the form.
      const response = await API.put(`/api/medical-cases/${id}`, formData, {
        handlingErrorLocally: true,
      });
      return response.data; // Return the updated case object from server
    } catch (error) {
      console.error(`Error updating case ${id}:`, error);
      throw error;
    }
  },

  remove: async (id) => {
    // Remove case by id - Maps to DELETE /api/medical-cases/:id
    try {
      const response = await API.delete(`/api/medical-cases/${id}`);
      // Return based on server response, assuming success if no error
      return { success: true, id, data: response.data };
    } catch (error) {
      console.error(`Error deleting case ${id}:`, error);
      throw error;
    }
  },

  // Fetch all distinct tags from the server
  getAllTags: async () => {
    // Maps to GET /api/medical-cases/tags
    try {
      const response = await API.get('/api/medical-cases/tags');
      // The server returns an array of strings. Map them to the { id, name } structure if needed by UI components.
      // Using the tag string itself as the 'id' and 'name' for simplicity here.
      return response.data.map(tag => ({ id: tag, name: tag }));
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  },

  getAutocompleteData: async () => {
    try {
      const response = await API.get('/api/system/autocomplete');
      return response; // Return the whole response
    } catch (error) {
      console.error('Error fetching autocomplete data:', error);
      throw error;
    }
  },

  // Add favorite/unfavorite functions based on API routes
  favorite: async (id) => {
    // Maps to POST /api/medical-cases/:id/favorite
    try {
      const response = await API.post(`/api/medical-cases/${id}/favorite`);
      return response.data;
    } catch (error) {
      console.error(`Error favoriting case ${id}:`, error);
      throw error;
    }
  },

  unfavorite: async (id) => {
    // Maps to DELETE /api/medical-cases/:id/favorite
    try {
      const response = await API.delete(`/api/medical-cases/${id}/favorite`);
      return response.data;
    } catch (error) {
      console.error(`Error unfavoriting case ${id}:`, error);
      throw error;
    }
  }
};
